import React, { useMemo, useState } from "react";
import { Table, ConfigProvider } from "antd";
import { Box, CircularProgress, Paper, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import TableHeaderAndActions from "../GenericDraggableTable/TableHeaderAndActions";
import { StyledTableContainer } from "../GenericDraggableTable/GenericDraggableTable";
import TableHeader from "../GenericDraggableTable/Renderers/TableHeader";
import EmailComposer from "../GenericDraggableTable/EmailComposerDialog";
import { BatchActionIconButton } from "../GenericDraggableTable/TableStyles";
import { DownloadFileIcon } from "../Icons";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import DownloadCSV from "../../../Helper/CSVOperations/DownloadCSV";
import Loader from "../../../Helper/Loaders/Loader";
import NoData from "../../../Helper/Loaders/NoData";
import { ThemeProperties } from "../../../Theme/ThemeProperties";
import { EMPTY_VALUE } from "../../../Helper/Formatters/PivotValueFormatter";

// Generic month-pivot table: a sticky first "Metric" column, one column per
// month (generated by the caller, forecast months tinted) and one row per
// configured metric, as a flat list. Above the table, one card holds the
// caller's filters (label above each control) and, right-aligned in the same
// row, the generic table header's own Download (BatchActionIconButton +
// DownloadFileIcon) and Email (EmailComposer's default trigger) icon buttons.
// The header row keeps the title and the caller's right-hand view (e.g. an
// identifier box). Nothing here is domain-specific: cell contents come from
// `renderCell`, labels from the caller.

export const FORECAST_COLUMN_CLASS = "pivot-forecast-col";
// One height for every control in the filter card (selects, toggles).
export const PIVOT_CONTROL_HEIGHT = 38;
export const PIVOT_MONTH_COLUMN_WIDTH = 150;
// Filter card border (the DT tab containers' light grey).
const FILTER_CARD_BORDER = "#E6E6E6";
// Forecast columns: body cells use the light purple tint; the header cell one
// step darker (the existing purple_tabs token) so it stands apart from its body
// the way a grey header does from a white body.
export const FORECAST_BODY_TINT = ThemeProperties.backgroundPurple;
export const FORECAST_HEADER_TINT = ThemeProperties.purple_tabs;

// Same body container as GenericDraggableTable (cell padding, header padding,
// row hover); only the forecast column tint is added.
// The body selectors also match the hovered row and are more specific than
// StyledTableContainer's ".ant-table-row:hover > .ant-table-cell" (#FBFBFB
// !important), so the forecast tint survives row hover. Exported for tests.
export const PIVOT_FORECAST_STYLES = {
    [`.ant-table-thead > tr > th.${FORECAST_COLUMN_CLASS}`]: {
        backgroundColor: `${FORECAST_HEADER_TINT} !important`,
    },
    [`.ant-table-tbody > tr.ant-table-row > td.${FORECAST_COLUMN_CLASS}, .ant-table-tbody > tr.ant-table-row:hover > td.${FORECAST_COLUMN_CLASS}`]: {
        backgroundColor: `${FORECAST_BODY_TINT} !important`,
    },
};
const BodyContainer = styled(StyledTableContainer)(PIVOT_FORECAST_STYLES);

// Body text: the generic table's 'text' / 'number' renderer variant.
const BODY_TEXT_VARIANT = "fw400s14lh24c3D4B5A";
// Second line: the generic table has no secondary-text renderer; this is the
// app's existing 12px grey caption variant.
const SECONDARY_TEXT_VARIANT = "fw400s12c686868";

/** Default cell: `text` (or an em-dash) with an optional `subText` line. */
export const DefaultPivotCell = ({ cell }) => (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant={BODY_TEXT_VARIANT}>{cell?.text ?? EMPTY_VALUE}</Typography>
        {cell?.subText && <Typography variant={SECONDARY_TEXT_VARIANT}>{cell.subText}</Typography>}
    </Box>
);

// Column headers use the generic table's own TableHeader (same variant, margins
// and ellipsis). Forecast columns are marked by their darker header tint only.
const MonthHeader = ({ column }) => <TableHeader config={{ title: column.label, dataIndex: column.key }} />;

// First column: the generic table's body text variant, like its first column;
// a sub-label follows on the same line in the caption style ("String 1 SOH").
const MetricCell = ({ row }) => (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", flexWrap: "wrap" }}>
        <Typography variant={BODY_TEXT_VARIANT}>{row.label}</Typography>
        {row.subLabel && <Typography variant={SECONDARY_TEXT_VARIANT}>{row.subLabel}</Typography>}
    </Box>
);

/** A labelled control in the filter card: label (+ optional help node) above, control below. */
const CardField = ({ label, help, children, testId }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }} data-testid={testId}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minHeight: "16px" }}>
            <Typography variant="fw400s12c686868" sx={{ lineHeight: "16px" }}>{label}</Typography>
            {help}
        </Box>
        {children}
    </Box>
);

/**
 * Props
 *   title                 header title
 *   columns               [{ key, label, isForecast }] months, oldest -> newest
 *   rows                  flat row model: [{ key, label, subLabel?, cells: { [monthKey]: cell } }]
 *                         (cell models from pivotModelHelpers or the caller)
 *   metricLabel           first column header (default t("Metric"))
 *   renderCell            (cell, row, column) => node; default DefaultPivotCell
 *   isLoading, emptyMessage (already translated; default t("No data available"))
 *   filters               [{ key, label, node, help? }] controls shown in the filter card, label
 *                         above each; `help` (e.g. an InfoIconTooltip) sits after the label
 *   footer                node rendered under the table
 *   disableDownloads      hides Download;  showEmailComposer  shows Email
 *   actionsEnabled        enables Download / Email
 *   getCSVData()          -> Promise<rows>; getCSVFileName() -> string
 *   onSendEmail({ sendTo, sendCc, subject, body }), availableEmailDomains, emailInfo
 *   customRightActionsView  node at the right of the header row (e.g. an identifier box)
 *   testIdPrefix          prefix for this table's test ids (default "pivot"):
 *                         <p>-filter-card, <p>-filter-<key>, <p>-download-csv-btn,
 *                         <p>-email-btn, <p>-month-<YYYY-MM>
 * Labels and row labels are rendered as given: the caller translates its own
 * text and keeps backend / org-meta text out of t().
 */
const MonthlyHistoricalPivotTable = ({
    title,
    columns = [],
    rows = [],
    metricLabel,
    renderCell,
    isLoading = false,
    emptyMessage,
    filters = [],
    footer = null,
    disableDownloads = false,
    showEmailComposer = true,
    actionsEnabled = true,
    getCSVData,
    getCSVFileName,
    onSendEmail,
    availableEmailDomains = [],
    emailInfo = {},
    customRightActionsView = null,
    testIdPrefix = "pivot",
}) => {
    const { t } = useTranslation(["common", "generic_table"]);
    const [isDownloading, setIsDownloading] = useState(false);

    const onDownloadCSV = async () => {
        if (isDownloading || !getCSVData) return;
        setIsDownloading(true);
        try {
            const csvRows = await getCSVData();
            if (Array.isArray(csvRows) && csvRows.length) {
                DownloadCSV(getCSVFileName ? getCSVFileName() : "download", csvRows);
            }
        } catch (error) {
            console.error("Error downloading CSV:", error);
        } finally {
            setIsDownloading(false);
        }
    };

    const antdColumns = useMemo(() => {
        const Cell = renderCell || ((cell) => <DefaultPivotCell cell={cell} />);
        return [
            {
                key: "__metric",
                title: <TableHeader config={{ title: metricLabel ?? t("Metric"), dataIndex: "__metric" }} />,
                fixed: "left",
                width: 200,
                render: (_value, row) => <MetricCell row={row} />,
            },
            ...(columns || []).map((column) => ({
                key: column.key,
                title: <MonthHeader column={column} />,
                width: PIVOT_MONTH_COLUMN_WIDTH,
                className: column.isForecast ? FORECAST_COLUMN_CLASS : undefined,
                onHeaderCell: () => ({ "data-testid": `${testIdPrefix}-month-${column.key}` }),
                render: (_value, row) => Cell(row?.cells?.[column.key], row, column),
            })),
        ];
    }, [columns, metricLabel, renderCell, t, testIdPrefix]);

    const visibleFilters = (Array.isArray(filters) ? filters : []).filter((f) => f && f.node);
    const showDownload = !disableDownloads;
    const hasActions = showDownload || showEmailComposer;
    const downloadActive = actionsEnabled && !isDownloading;

    return (
        <Paper
            elevation={0}
            data-testid="monthly-historical-pivot-table"
            sx={{ px: 3, py: 2, height: "100%", borderRadius: "12px", border: `1px solid ${ThemeProperties.backgroundPurpleDarker}` }}
        >
            {/* Header row: title + caller's right-hand view only. */}
            <TableHeaderAndActions
                title={title}
                disableSearch={true}
                showPrimaryButton={false}
                selectedRows={[]}
                totalRowSelectionCount={0}
                disableDownloads={true}
                customRightActions={!!customRightActionsView}
                customRightActionsView={customRightActionsView}
            />

            {(visibleFilters.length > 0 || hasActions) && (
                <Box
                    data-testid={`${testIdPrefix}-filter-card`}
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "16px",
                        border: `1px solid ${FILTER_CARD_BORDER}`,
                        borderRadius: "10px",
                        px: 2,
                        py: 1.5,
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: "16px" }}>
                        {visibleFilters.map((filter) => (
                            <CardField key={filter.key} label={filter.label} help={filter.help} testId={`${testIdPrefix}-filter-${filter.key}`}>
                                {filter.node}
                            </CardField>
                        ))}
                    </Box>

                    {hasActions && (
                        // Same height as the controls so the icons line up with them.
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", ml: "auto", height: PIVOT_CONTROL_HEIGHT }}>
                            {/* Download: the generic table header's button, as in TableHeaderAndActions. */}
                            {showDownload && (
                                <PrimaryTooltip title={isDownloading ? <>Downloading...</> : <>Download Data</>} placement="bottom">
                                    <BatchActionIconButton
                                        data-testid={`${testIdPrefix}-download-csv-btn`}
                                        onClick={onDownloadCSV}
                                        active={downloadActive}
                                        disabled={!downloadActive}
                                    >
                                        {isDownloading ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <DownloadFileIcon fontSize="inherit" />
                                        )}
                                    </BatchActionIconButton>
                                </PrimaryTooltip>
                            )}
                            {/* Email: EmailComposer with its default icon trigger. */}
                            {showEmailComposer && (
                                <EmailComposer
                                    triggerTestId={`${testIdPrefix}-email-btn`}
                                    active={actionsEnabled}
                                    selectedRows={[]}
                                    availableEmailDomains={availableEmailDomains}
                                    emailInfo={emailInfo}
                                    getCSVData={getCSVData}
                                    onDownloadCSV={onDownloadCSV}
                                    onSendEmail={onSendEmail}
                                />
                            )}
                        </Box>
                    )}
                </Box>
            )}

            <ConfigProvider>
                <BodyContainer>
                    <Table
                        bordered
                        rowKey="key"
                        columns={antdColumns}
                        dataSource={isLoading ? [] : (rows || [])}
                        pagination={false}
                        loading={{ spinning: isLoading, indicator: <Loader /> }}
                        locale={{ emptyText: !isLoading && <NoData text={emptyMessage ?? t("No data available")} /> }}
                        scroll={{ x: "max-content" }}
                    />
                </BodyContainer>
            </ConfigProvider>

            {footer}
        </Paper>
    );
};

export default MonthlyHistoricalPivotTable;
